import { Button } from 'antd'
import { useState } from 'react'

import { BODY_PARTS, COLOR, wait } from '../../../constants'
import { EligibilityTable } from '../eligibility/EligibilityTable'
import { BodyPartSelector } from './BodyPartSelector'
import { ENDPOINTS } from '../../../service/ENDPOINTS'
import { useParams } from 'react-router-dom'
import { getReq } from '../../../service/API'
import { useNextPhase } from '../../../hooks/useNextPhase'
import { useMoveSubjectForward, useSubject } from '../../../hooks/useSubject'
import { FormLabel } from '../../../components/form/FormLabel'

type Props = {
  navigateToTerminate: () => void
  closeDrawer: () => void
}

export const EligibilityMoveView = ({ navigateToTerminate, closeDrawer }: Props) => {
  const { projectId, subjectId } = useParams() as { projectId: string; subjectId: string }

  const [isBlurred, setIsBlurred] = useState(true)

  const { data: subjectData } = useSubject(projectId, subjectId)
  const { callMoveForward, isLoading } = useMoveSubjectForward(projectId, subjectId)
  const { data: nextPhase } = useNextPhase(projectId, subjectId)

  const [loadingEligibility, setLoadingEligibility] = useState<boolean>(false)
  const [selectedBodyPart, setSelectedBodyPart] = useState<typeof BODY_PARTS | undefined>(undefined)

  const [eligibility, setEligibility] = useState<any>(dummyTableData)

  const eligibleBodyParts =
    nextPhase && nextPhase.bodyParts
      ? nextPhase.bodyParts.map((item) => {
          return { bodyPart: item, label: BODY_PARTS[item] || item }
        })
      : []

  const populateTable = async () => {
    if (loadingEligibility) {
      return
    }

    setLoadingEligibility(true)

    await wait(500)

    return getReq(ENDPOINTS.SUBJECTS.eligibility(projectId, subjectId))
      .then((response) => {
        console.log('EligibilityMoveView > response = ', response.data)

        setLoadingEligibility(false)
        setIsBlurred(false)
        setEligibility(response.data)
      })
      .catch((error) => {
        setLoadingEligibility(false)
        setIsBlurred(false)
        alert('Error fetching eligibility data: ' + error.message)
      })
  }

  const handleMove = () => {
    const obj = {
      subjectId: subjectData._id,
      bodyPart: selectedBodyPart,
      projectId: projectId,
    }

    return callMoveForward(obj)
      .then((result) => {
        closeDrawer()
        return result
      })
      .catch((error) => {
        console.error('Error moving subject forward:', error)
      })
  }

  return (
    <div className="col-12  " style={{ background: '#f8f8f8' }}>
      <div className="row" style={{}}>
        <div className="col-10 mx-auto" style={{ minWidth: 500 }}>
          <h5 className="my-4">
            <span className="fw-semibold">Move to</span>{' '}
            <span className="fw-bolder text-primary"> {nextPhase?.title || 'noty tiasdsad'} </span>{' '}
            <span className="fw-semibold">based on eligibility</span>
          </h5>

          <div
            className="col-12 bg-white border rounded px-0 mt-3"
            style={{ boxShadow: '0px 2px 15px -1px rgba(0, 0, 0, 0.08)' }}
          >
            <div className="border-bottom px-4 py-3">
              <p className="fw-semibold opacity-75 p-small ">1. Evaluate eligibility</p>
            </div>
            <div className="p-4">
              <h5 className="mb-2 ">Subject eligibility calculation</h5>
              <p>
                Eligibility for{' '}
                <span
                  className="fw-bold px-2 rounded me-1"
                  style={{ paddingTop: 0, paddingBottom: 2, background: COLOR.LIGHT_GRAY }}
                >
                  {subjectData.displayId}
                </span>
                has been calculated based on their responses. Before proceeding, please review the key metrics from
                their eligibility period. Values highlighted in red indicate areas where the reported data does not meet
                the eligibility criteria. Please review these details carefully.
              </p>

              <div className="mt-3 position-relative">
                <FormLabel label="Eligibility matrix:"></FormLabel>
                {isBlurred && (
                  <div
                    className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center"
                    style={{ zIndex: 1 }}
                  >
                    <Button
                      type="primary"
                      className="px-4"
                      loading={loadingEligibility}
                      onClick={populateTable}
                      style={{ boxShadow: '0px 4px 20px 10px rgba(0, 0, 0, 0.08)' }}
                    >
                      View Eligibility
                    </Button>
                  </div>
                )}
                <div
                  className="px-1"
                  style={{
                    filter: isBlurred ? 'blur(2px)' : 'none',
                  }}
                >
                  <EligibilityTable data={eligibility} mutateEligibility={() => {}} />
                </div>
              </div>
            </div>
          </div>

          <div
            className="col-12 bg-white border rounded px-0 mt-4"
            style={{ boxShadow: '0px 2px 15px -1px rgba(0, 0, 0, 0.08)' }}
          >
            <div className="border-bottom px-4 py-3">
              <p className="p-small opacity-75 fw-bold">2. Confirm target</p>
            </div>
            <div className="p-4">
              <h5 className="mb-2 ">Select the target bodypart</h5>
              <p>To advance the Subject to the next study phase, select the correct target body part below.</p>

              <div className="mt-3">
                <BodyPartSelector
                  className="row"
                  bodyparts={eligibleBodyParts}
                  setSelectedBodyPart={setSelectedBodyPart}
                />
              </div>
            </div>
          </div>

          <div className="col-12 d-flex justify-content-between my-3 ">
            <div className="">
              <Button type="default" danger onClick={navigateToTerminate} className="me-2">
                Study Termination
              </Button>
            </div>
            <div className="d-flex justify-content-end">
              <Button className="me-2 px-5" onClick={closeDrawer}>
                Cancel
              </Button>
              <Button
                className="px-5"
                type="primary"
                disabled={!selectedBodyPart}
                onClick={handleMove}
                loading={isLoading}
              >
                Move to Next Phase
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const dummyTableData = {
  calculation: [
    {
      bodypart: '-',
      deviation: '-',
      mean: '-',
      values: [],
    },
    {
      bodypart: '-',
      deviation: '-',
      mean: '-',
      values: [],
    },
  ],
  eligibility: {
    days: 0,
    minDays: 0,
    averageMin: 0,
    averageMax: 0,
    deviationMin: 0,
    deviationMax: 0,
    rounding: 10 | 100 | 1000,
  },
}
